import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, EventType, AccountInfo } from '@azure/msal-browser';
import { environment } from '../../../environments/environment';
import { TokenClaims } from '@sal/interfaces';
import { IUser } from '../../shared/interfaces/IUser';
import { Role } from '../../shared/enums/role.enum';
import { TenantAuthorizationService } from './tenant-authorization.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly msalService = inject(MsalService);
    private readonly msalBroadcastService = inject(MsalBroadcastService);
    private readonly tenantAuthorizationService = inject(TenantAuthorizationService);

    private readonly interactionStatus = toSignal(this.msalBroadcastService.inProgress$);
    private readonly msalEvent = toSignal(this.msalBroadcastService.msalSubject$);

    private readonly _userAccount = signal<AccountInfo | null>(null);
    private readonly _userPhotoResource = httpResource.blob(
        () => {
            const oid = this.userIdTokenClaims()?.oid;
            return oid ? `${environment.apiConfig[1].uri}/users/${oid}/photo/$value` : undefined;
        },
        { parse: blob => URL.createObjectURL(blob) }
    );

    public readonly isAuthenticated = computed(() => this._userAccount() !== null);
    public readonly isAuthLoading = computed(() => this.interactionStatus() !== InteractionStatus.None);
    public readonly userIdTokenClaims = computed(() => (this._userAccount()?.idTokenClaims as TokenClaims) ?? null);
    public readonly user = computed<IUser | null>(() => this.createUser(this.userIdTokenClaims()));
    public readonly role = computed(() => this.mapToRole(this.user()?.roles ?? []));
    public readonly userPhoto = computed(() => this._userPhotoResource.value());
    public readonly userName = computed(() => {
        const claims = this.userIdTokenClaims();
        return claims ? `${claims.preferred_family_name}, ${claims.preferred_given_name}` : '';
    });

    constructor() {
        effect(() => this.onInteractionStatusChange());
        effect(() => this.onMsalEvent());
    }

    public logout(): void {
        this.tenantAuthorizationService.clearActiveTenantRole();
        this.msalService.logoutRedirect();
    }

    public isAdmin(): boolean {
        return this.role() === Role.Administrator;
    }

    public hasRole(role: Role): boolean {
        return this.role() === role;
    }

    public getRole(): Role {
        return this.role();
    }

    private onInteractionStatusChange(): void {
        if (this.interactionStatus() === InteractionStatus.None) {
            this.resolveActiveAccount();
        }
    }

    private onMsalEvent(): void {
        if (this.msalEvent()?.eventType === EventType.LOGOUT_SUCCESS) {
            this._userAccount.set(null);
            this.tenantAuthorizationService.clearActiveTenantRole();
        }
    }

    private resolveActiveAccount(): void {
        const accounts = this.msalService.instance.getAllAccounts();
        const activeAccount = this.msalService.instance.getActiveAccount() ?? accounts[0] ?? null;

        this._userAccount.set(activeAccount);

        if (activeAccount) {
            this.msalService.instance.setActiveAccount(activeAccount);

            const claims = activeAccount.idTokenClaims as TokenClaims | undefined;
            if (claims) {
                const activeTenantRole = this.tenantAuthorizationService.resolveActiveTenantRole(claims);
                if (activeTenantRole) {
                    this.tenantAuthorizationService.storeActiveTenantRole(activeTenantRole);
                } else {
                    this.tenantAuthorizationService.clearActiveTenantRole();
                }
            }
        } else {
            this.tenantAuthorizationService.clearActiveTenantRole();
        }
    }

    private createUser(claims: TokenClaims | null): IUser | null {
        if (!claims) {
            return null;
        }

        return {
            userid: claims['employee_id'] || claims['gid'] || claims['oid'] || '',
            department: claims['org_code'] || claims['org_id'] || '',
            firstname: claims['preferred_given_name'] || claims['given_name'] || '',
            surname: claims['preferred_family_name'] || claims['family_name'] || '',
            email: claims['preferred_username'] || claims['mail'] || '',
            roles: claims.roles ?? []
        };
    }

    private mapToRole(roles: string[]): Role {
        return roles.includes('Tenant.Read.All') ? Role.Administrator : Role.User;
    }
}
